import React, { useEffect } from "react";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { useAuth } from "./AuthContext";
import { detectWebView } from "../utils/webViewUtils";
import { APP_VERSION } from "../config/version";
import { EnvironmentConfig } from "../config/environment";
import { SENSITIVE_URL_PARAMS, redactEventCredentials } from "../analytics/credentialRedaction";
import { DEMO_EVENTS } from "../analytics/events";
import { getDemoSession, isDemo, isDemoHost } from "../config/demo";
import type { CaptureResult } from "posthog-js";

/**
 * In the Live sample only the three demo events leave the browser (PRD 117,
 * D10): no pageviews, no autocapture, no identify. Checked per event so a demo
 * session started after init (or ended) is honoured without a reload.
 */
export const demoAwareBeforeSend = (event: CaptureResult | null): CaptureResult | null => {
  if (event && isDemo() && !(DEMO_EVENTS as readonly string[]).includes(event.event)) return null;
  return redactEventCredentials(event);
};

/**
 * A PostHog project key is a *client* credential — it only permits writing
 * events and reading feature flags, never reading data back out, and it ships
 * in the JS bundle regardless. So the literal default below is not a leak.
 *
 * It IS a defence against the failure that put us here: this key previously
 * lived only in deploy config, went missing, and web analytics were silently
 * dark for months. CRA inlines REACT_APP_* at build time, so a missing var
 * produces a working build that emits nothing. The literal makes that
 * impossible; the env var remains as an override for pointing a build at a
 * different project. Mirrors mobile/src/analytics/posthog.ts.
 */
const POSTHOG_API_KEY =
  process.env.REACT_APP_POSTHOG_API_KEY ||
  "phc_S00dHKRRsfIrqTS0n8ehq0ND3nGDHCwT6Jz6YMPA7Oy";
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

const BRIDGED_DISTINCT_ID_KEY = "ph_bridged_did";

/**
 * The mobile app opens this app inside a WebView for registration and login
 * (see mobile AuthWebView), passing its PostHog distinct id as `ph_did`.
 * Bootstrapping from it keeps the mobile person and the webview person the
 * same, instead of fragmenting the funnel at the platform boundary.
 *
 * Persisted in sessionStorage because the user client-side-navigates away from
 * the URL that carried the param (/register → /subscribe) well before the
 * interesting events fire.
 */
const resolveBridgedDistinctId = (): string | null => {
  try {
    const fromQuery = new URLSearchParams(window.location.search).get("ph_did");
    if (fromQuery) {
      sessionStorage.setItem(BRIDGED_DISTINCT_ID_KEY, fromQuery);
      return fromQuery;
    }
    return sessionStorage.getItem(BRIDGED_DISTINCT_ID_KEY);
  } catch {
    // Private-mode / disabled storage — fall back to a normal anonymous id.
    return null;
  }
};

const bridgedDistinctId = typeof window !== "undefined" ? resolveBridgedDistinctId() : null;

// Initialize PostHog once at module level
if (POSTHOG_API_KEY) {
  posthog.init(POSTHOG_API_KEY, {
    api_host: POSTHOG_HOST,
    // The app uses createBrowserRouter; "history_change" is the explicit
    // setting for client-side routing.
    capture_pageview: "history_change",
    capture_pageleave: true,
    persistence: "localStorage+cookie",
    ...(bridgedDistinctId
      ? { bootstrap: { distinctID: bridgedDistinctId, isIdentifiedID: false } }
      : {}),
    // The mobile hand-off's /subscribe?return_url=… carries live tokens
    // (analytics/credentialRedaction). Masked where posthog-js reads the URL,
    // so they never reach its cookie or the session-entry and initial-person
    // URLs it stores; then every string of every event is checked once more
    // on the way out, which also covers values stored before this shipped.
    // Masking personal data also masks ad click ids (gclid, fbclid…).
    mask_personal_data_properties: true,
    custom_personal_data_properties: [...SENSITIVE_URL_PARAMS],
    before_send: demoAwareBeforeSend,
    // The demo host records nothing it does not have to.
    ...(isDemoHost()
      ? { autocapture: false, capture_pageview: false, capture_pageleave: false, disable_session_recording: true }
      : {}),
  });

  // Separate webview traffic from ordinary browser traffic in reporting.
  const { isWebView, platform } = detectWebView();
  posthog.register({
    surface: isWebView ? "mobile_webview" : "web_browser",
    platform: platform || "web",
    app_version: APP_VERSION,
    // QA and production share one PostHog project, so without this every
    // funnel number silently mixes our own QA run-throughs with real users.
    // Sourced from EnvironmentConfig (not NODE_ENV) so it tracks the env
    // switcher — the environment whose API we're talking to is the one whose
    // data we're generating.
    environment: EnvironmentConfig.getEnvironment().id,
  });
  const demoSession = getDemoSession();
  if (demoSession) posthog.register({ demo_persona: demoSession.persona });
}

/**
 * Root provider — wrap the entire app with this once, outside AuthProvider.
 */
export const PostHogProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  if (!POSTHOG_API_KEY) {
    return <>{children}</>;
  }

  return <PHProvider client={posthog}>{children}</PHProvider>;
};

/**
 * Place this component inside AuthProvider.
 * It identifies the user with PostHog so flags can be targeted,
 * and resets the session on logout.
 */
export const PostHogIdentifier: React.FC = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (!POSTHOG_API_KEY) return;

    // Never identify the Live sample's shared user (PRD 117, D10): every
    // visitor of a persona signs in as the same uuid.
    if (user?.uuid && isDemo()) return;

    if (user?.uuid) {
      const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ");
      const { platform } = detectWebView();
      const os = platform === "ios" ? "iOS" : platform === "android" ? "Android" : "Web";
      posthog.identify(user.uuid, {
        ...(user.email ? { email: user.email } : {}),
        ...(user.phone_number ? { phone: user.phone_number } : {}),
        ...(fullName ? { name: fullName } : {}),
        ...(user.beta !== undefined ? { beta: user.beta } : {}),
        $os: os,
      });
    } else if (!user) {
      posthog.reset();
    }
  }, [user?.uuid, user?.email, user?.phone_number, user?.first_name, user?.last_name, user?.beta, user]);

  return null;
};
